import { makeFetchTransport } from '@sentry/browser';
import type {
  Envelope,
  ReplayEnvelope,
  ReplayEvent,
  Transport,
  TransportMakeRequestResponse,
} from '@sentry/core';
import {
  createReplayPolicyState,
  filterReplayMetadata,
  filterReplayRecordingEvent,
} from './sentry-replay-policy';
import type {
  ReplayPolicyState,
  ReplayRecordingEvent,
} from './sentry-replay-policy';

const MAX_BYTES = 12_000_000;
const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });
const probes = new Map<
  string,
  { runId: string; received: (status?: number, blocked?: boolean) => void }
>();

export function registerReplayProbe(
  replayId: string,
  runId: string,
  received: (status?: number, blocked?: boolean) => void,
): () => void {
  if (
    !/^[\da-f]{32}$/i.test(replayId) ||
    !/^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(runId)
  )
    throw new Error('Invalid replay probe markers');
  probes.set(replayId, { runId, received });
  return () => probes.delete(replayId);
}

/** Preserve the SDK's gzip recording format, with a bounded decoded size. */
async function codec(
  bytes: Uint8Array,
  compress: boolean,
  format: 'gzip' | 'deflate',
): Promise<Uint8Array> {
  const transform = compress
    ? new CompressionStream(format)
    : new DecompressionStream(format);
  const source = new ReadableStream<Uint8Array<ArrayBuffer>>({
    start(controller) {
      controller.enqueue(new Uint8Array(bytes));
      controller.close();
    },
  });
  const reader = source.pipeThrough(transform).getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    size += value.length;
    if (size > MAX_BYTES) {
      await reader.cancel();
      throw new Error('Replay size limit exceeded');
    }
    chunks.push(value);
  }
  const result = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

export async function filterReplayRecording(
  payload: unknown,
  state: ReplayPolicyState,
): Promise<string | Uint8Array> {
  if (typeof payload !== 'string' && !(payload instanceof Uint8Array))
    throw new Error('Unsupported replay payload');
  const bytes = typeof payload === 'string' ? encoder.encode(payload) : payload;
  if (bytes.length > MAX_BYTES) throw new Error('Replay size limit exceeded');
  const newline = bytes.indexOf(10);
  if (newline < 0 || newline > 1024)
    throw new Error('Unsupported replay header');
  const header = JSON.parse(decoder.decode(bytes.subarray(0, newline))) as {
    segment_id?: unknown;
  };
  if (
    typeof header.segment_id !== 'number' ||
    !Number.isInteger(header.segment_id) ||
    header.segment_id < 0
  )
    throw new Error('Unsupported replay segment');
  const data = bytes.subarray(newline + 1);
  const compressed = typeof payload !== 'string' && data[0] !== 91;
  const format = data[0] === 31 && data[1] === 139 ? 'gzip' : 'deflate';
  const text = decoder.decode(
    compressed ? await codec(data, false, format) : data,
  );
  const events: unknown = JSON.parse(text);
  if (!Array.isArray(events) || events.length > 100000)
    throw new Error('Unsupported replay event list');
  const safe = events
    .map((event) =>
      filterReplayRecordingEvent(event as ReplayRecordingEvent, state),
    )
    .filter((event) => event !== null);
  const body = encoder.encode(JSON.stringify(safe));
  if (body.length > MAX_BYTES) throw new Error('Replay size limit exceeded');
  const prefix = encoder.encode(
    `${JSON.stringify({ segment_id: header.segment_id })}\n`,
  );
  const recording = compressed ? await codec(body, true, format) : body;
  const output = new Uint8Array(prefix.length + recording.length);
  output.set(prefix);
  output.set(recording, prefix.length);
  return typeof payload === 'string' ? decoder.decode(output) : output;
}

/** Replay bypasses the client's beforeEnvelope hook; the public transport covers every segment. */
export function replayPrivacyTransport(
  delegate: Transport,
  onBlocked: () => void = () => undefined,
): Transport {
  const states = new Map<string, ReplayPolicyState>();
  const pending = new Set<Promise<TransportMakeRequestResponse>>();
  function send(envelope: Envelope): PromiseLike<TransportMakeRequestResponse> {
    const hasReplay = envelope[1].some(
      (item) =>
        item[0].type === 'replay_event' || item[0].type === 'replay_recording',
    );
    if (!hasReplay) return delegate.send(envelope);
    const job = (async () => {
      const metadata = envelope[1].find(
        (item) => item[0].type === 'replay_event',
      )?.[1] as Record<string, unknown> | undefined;
      const replayId = metadata?.['replay_id'];
      let filtered: Envelope;
      try {
        if (
          typeof replayId !== 'string' ||
          !/^[\da-f]{32}$/i.test(replayId) ||
          !envelope[1].some((item) => item[0].type === 'replay_recording')
        )
          throw new Error('Incomplete replay segment');
        const state = states.get(replayId) ?? createReplayPolicyState();
        states.set(replayId, state);
        if (states.size > 4) {
          const oldest = states.keys().next().value;
          if (oldest) states.delete(oldest);
        }
        const safe = filterReplayMetadata(metadata as Record<string, unknown>);
        const probe = probes.get(replayId);
        if (probe)
          safe['tags'] = {
            ...(safe['tags'] as Record<string, unknown>),
            source: 'sentry_replay_test',
            test_run_id: probe.runId,
            test_scenario: 'replay_filtering',
          };
        const rawRecording = envelope[1].find(
          (item) => item[0].type === 'replay_recording',
        )?.[1];
        const recording = await filterReplayRecording(rawRecording, state);
        const items: ReplayEnvelope[1] = [
          [{ type: 'replay_event' }, safe as unknown as ReplayEvent],
          [
            {
              type: 'replay_recording',
              length:
                typeof recording === 'string'
                  ? encoder.encode(recording).length
                  : recording.length,
            },
            recording,
          ],
        ];
        // Replay has no cross-service sampling payload. Do not copy arbitrary envelope fields.
        filtered = [
          {
            event_id: replayId,
            sent_at: new Date().toISOString(),
            ...(typeof envelope[0].dsn === 'string'
              ? { dsn: envelope[0].dsn }
              : {}),
          },
          items,
        ];
      } catch {
        onBlocked();
        if (typeof replayId === 'string')
          probes.get(replayId)?.received(undefined, true);
        // Local failure response: do not send or let Replay retry an unsafe payload.
        return { statusCode: 0 };
      }
      const response = await delegate.send(filtered);
      if (typeof replayId === 'string')
        probes.get(replayId)?.received(response.statusCode);
      return response;
    })();
    pending.add(job);
    job.then(
      () => pending.delete(job),
      () => pending.delete(job),
    );
    return job;
  }
  return {
    send,
    async flush(timeout) {
      const started = Date.now();
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const finished = Promise.all(
          [...pending].map((job) =>
            job.then(
              () => true,
              () => false,
            ),
          ),
        ).then((results) => results.every(Boolean));
        const done =
          timeout === undefined
            ? await finished
            : await Promise.race([
                finished,
                new Promise<boolean>((resolve) => {
                  timer = setTimeout(() => resolve(false), timeout);
                }),
              ]);
        if (!done) return false;
        return await delegate.flush(
          timeout === undefined
            ? undefined
            : Math.max(0, timeout - (Date.now() - started)),
        );
      } finally {
        if (timer !== undefined) clearTimeout(timer);
      }
    },
  };
}

export const sentryReplayTransport = (
  options: Parameters<typeof makeFetchTransport>[0],
): Transport =>
  replayPrivacyTransport(makeFetchTransport(options), () =>
    options.recordDroppedEvent('before_send', 'replay'),
  );
